/**
 * Isometric line drawings for the advertising process (Annonsröntgen och mål,
 * Spårning och struktur, Budskap och tester, Optimera och skala). Same
 * projection, frame, stroke and animation hooks as the other service
 * drawings, with their own motifs and motion:
 * - 01 a lens slides along three ad boards; each lights up as it's checked
 * - 02 three channels are piped into one counter, line by line
 * - 03 three ad boards are tested; the winner fills in
 * - 04 a marker climbs a staircase of rising steps
 */

import type { CSSProperties } from "react";

import {
  box,
  CORNER_CROSSES,
  floorGrid,
  IllustrationFrame,
  motionPath,
  polyline,
  project,
  type Point3,
  type ProcessIllustration,
  type Shape,
} from "@/components/services/seo/seo-process-illustrations";

function dotAt(point: Point3) {
  const [cx, cy] = project(point);
  return { cx, cy };
}

/** A flat rectangle on a vertical face at depth y (spans x and z). */
function faceRect(x: number, y: number, z: number, w: number, h: number) {
  return polyline(
    [
      [x, y, z],
      [x + w, y, z],
      [x + w, y, z + h],
      [x, y, z + h],
    ],
    true,
  );
}

/** An ad board on two posts, facing the viewer at depth y. */
function board(x: number, y: number, w = 22, h = 16, lift = 14): Shape[] {
  return [
    ...box(x + 2, y, 4, 2, 2, lift),
    ...box(x + w - 4, y, 4, 2, 2, lift),
    ...box(x, y - 1, 4 + lift, w, 2, h),
  ];
}

const BOARD_Y = 50;
const BOARD_XS = [8, 38, 68];
const BOARD_FACE_Y = BOARD_Y - 1;

// ---------------------------------------------------------------- 01 Annonsröntgen och mål

const LENS_PATH: Point3[] = [
  [19, BOARD_FACE_Y, 26],
  [49, BOARD_FACE_Y, 26],
  [79, BOARD_FACE_Y, 26],
];

function AuditIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [...box(0, 30, 0, 100, 44, 4), ...BOARD_XS.flatMap((x) => board(x, BOARD_Y))];
  const construct = floorGrid(100, 20).filter((_, index) => index % 2 === 0);
  const [lensX, lensY] = project(LENS_PATH[LENS_PATH.length - 1]);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      {BOARD_XS.map((x, index) => (
        <circle
          key={x}
          className="cc-ill-ad-audit__dot"
          style={{ "--cc-ill-node": index } as CSSProperties}
          {...dotAt([x + 11, BOARD_FACE_Y, 30])}
          r={3}
        />
      ))}
      <g className="cc-ill-ad-audit__lens" transform={`translate(${lensX.toFixed(1)} ${lensY.toFixed(1)})`}>
        <circle cx={0} cy={0} r={9} />
        <path d="M6.5 6.5l7 7" />
        <animateMotion
          begin="indefinite"
          dur="1.8s"
          fill="freeze"
          calcMode="spline"
          keyTimes="0;1"
          keySplines="0.45 0 0.25 1"
          path={motionPath(LENS_PATH)}
        />
      </g>
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 02 Spårning och struktur

const SOURCES: Point3[] = [
  [14, 20, 12],
  [14, 50, 12],
  [14, 80, 12],
];
const COUNTER_TOP: Point3 = [78, 50, 22];

function TrackingIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 6, 0, 96, 88, 4),
    ...SOURCES.flatMap(([x, y]) => box(x - 5, y - 5, 4, 10, 10, 8)),
    ...box(68, 40, 4, 20, 20, 18),
  ];
  const construct = [polyline([[48, 6, 4], [48, 94, 4]])];

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <g className="cc-ill-ad-track__pipes">
        {SOURCES.map((source, index) => (
          <path
            key={index}
            d={polyline([source, [48, source[1], 12], [48, 50, 18], COUNTER_TOP])}
            pathLength={1}
            style={{ "--cc-ill-segment": index } as CSSProperties}
          />
        ))}
      </g>
      <circle className="cc-ill__dot" {...dotAt(COUNTER_TOP)} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 03 Budskap och tester

const WINNER = 1;

function TestsIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [...box(0, 30, 0, 100, 44, 4), ...BOARD_XS.flatMap((x) => board(x, BOARD_Y))];
  const construct = [polyline([[0, BOARD_FACE_Y, 34], [100, BOARD_FACE_Y, 34]])];

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      {BOARD_XS.map((x, index) => (
        <path
          key={x}
          className={
            index === WINNER ? "cc-ill-ad-test__face cc-ill-ad-test__face--winner" : "cc-ill-ad-test__face"
          }
          style={{ "--cc-ill-segment": index } as CSSProperties}
          d={faceRect(x + 2, BOARD_FACE_Y, 20, 18, 12)}
        />
      ))}
      <circle className="cc-ill__dot" {...dotAt([BOARD_XS[WINNER] + 11, BOARD_FACE_Y, 36])} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 04 Optimera och skala

const STAIRS = [0, 1, 2, 3, 4];
const STEP = 14;
const CLIMB: Point3[] = STAIRS.map((index) => [14 + index * STEP + 6, 46, 4 + (index + 1) * 7]);

function ScaleIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 26, 0, 100, 52, 4),
    ...STAIRS.flatMap((index) => box(14 + index * STEP, 40, 4, STEP, 12, (index + 1) * 7)),
  ];
  const construct = [polyline([[10, 52, 4 + 42], [90, 52, 4 + 42]])];
  const [endX, endY] = project(CLIMB[CLIMB.length - 1]);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <path className="cc-ill-ad-scale__trail" d={polyline(CLIMB)} pathLength={1} />
      <g className="cc-ill-ad-scale__marker" transform={`translate(${endX.toFixed(1)} ${endY.toFixed(1)})`}>
        <circle cx={0} cy={0} r={4} />
        <animateMotion
          begin="indefinite"
          dur="1.8s"
          fill="freeze"
          calcMode="spline"
          keyTimes="0;1"
          keySplines="0.45 0 0.25 1"
          path={motionPath(CLIMB)}
        />
      </g>
    </IllustrationFrame>
  );
}

export const AD_PROCESS_ILLUSTRATIONS: ProcessIllustration[] = [
  AuditIllustration,
  TrackingIllustration,
  TestsIllustration,
  ScaleIllustration,
];
